import React, { createContext, useContext, useEffect, useState } from "react";
import en from "../locales/en";
import am from "../locales/am";
import or from "../locales/or";

type Language = "en" | "am" | "or";

type Translations = typeof en;

const translations: Record<Language, Translations> = {
  en,
  am,
  or,
};

interface I18nContextProps {
  t: (key: keyof Translations) => string;
  language: Language;
  changeLanguage: (lang: Language) => void;
}

const I18nContext = createContext<I18nContextProps | undefined>(undefined);

export const TranslationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [language, setLanguage] = useState<Language>("en");

  useEffect(() => {
    const savedLang = localStorage.getItem("lang") as Language;
    if (savedLang && translations[savedLang]) {
      setLanguage(savedLang);
    }
  }, []);

  const changeLanguage = (lang: Language) => {
    if (translations[lang]) {
      setLanguage(lang);
      localStorage.setItem("lang", lang);
    }
  };

  const t = (key: keyof Translations): string => {
    return translations[language][key] || key;
  };

  return (
    <I18nContext.Provider value={{ t, language, changeLanguage }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useTranslation = (): I18nContextProps => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useTranslation must be used within TranslationProvider");
  }
  return context;
};
